import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import MainLayout from '../../layouts/MainLayout'
import '../../assets/styles/custom-styles.css'
import useLoader from '../../hooks/useLoader'
import { detailsMaintenanceFields } from '../../constants/forms/manutencao/maintenanceFields'
import DetailsSectionRenderer from '../../components/DetailsSectionRenderer'
import useForm from '../../hooks/useForm'
import { setDefaultFieldValues } from '../../utils/objectUtils'
import useBaseService from '../../hooks/services/useBaseService'
import { entities } from '../../constants/entities'
import PageHeader from '../../components/PageHeader'
import { formatDateFront } from '../../utils/formatDate'
import TableWithInactiveFilter from '../../components/table/TableWithInactiveFilter'
import { transformFiltersForUrl } from '../../utils/transformFiltersForUrl'
import useFilterHandlers from '../../hooks/useFilterHandlers'
import useUrlFilters from '../../hooks/useUrlFilters'
import useGenericFilter from '../../hooks/filters/useGenericFilter'
import { useSectionFields } from '../../hooks/useSectionFields'
import Button from '../../components/Button'
import AddItemMaintenanceModal from './AddItemMaintenanceModal'
import CreateGroupModal from './components/CreateGroupModal'
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  Autocomplete,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import { TechnicianStatus } from '../../enums/technician_status-enum'
import useQueryWithLoader from '../../hooks/useQueryWithLoader'
import { faPaperclip, faClipboardCheck } from '@fortawesome/free-solid-svg-icons'
import useNotification from '../../hooks/useNotification'
import { PermissionsContext } from '../../context/PermissionsContext'
import { hasRole } from '../../utils/permission'
import api from '../../services/api'
import { downloadBlob } from '../../utils/downloadBlob'

const DetailsMaintenancePage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { showLoader, hideLoader } = useLoader()
  const {
    getByColumn: baseServiceGetByColumn,
    get: baseServiceGet,
    post: baseServicePost,
  } =
    useBaseService(navigate)
  const queryClient = useQueryClient()
  const { showNotification } = useNotification()
  const { userRoles = [] } = useContext(PermissionsContext)
  const canDecideUpgrade = hasRole(
    ['Super Admin', 'Admin', 'Admin Sênior', 'Gestor'],
    userRoles
  )
  const { updateField, removeFields } = useSectionFields()
  const { formData, setFormData } = useForm(
    setDefaultFieldValues(detailsMaintenanceFields)
  )

  const [maintenanceFields] = detailsMaintenanceFields
  const [fields, setFields] = useState(detailsMaintenanceFields)

  const [openAddServiceMaintenanceModal, setOpenAddServiceMaintenanceModal] =
    useState(false)
  const [selectedServiceForDocument, setSelectedServiceForDocument] =
    useState(null)
  const [selectedUpgradeService, setSelectedUpgradeService] = useState(null)
  const [selectedUpgradeGroupId, setSelectedUpgradeGroupId] = useState('')
  const [selectedUpgradeGroup, setSelectedUpgradeGroup] = useState(null)
  const [selectedUpgradeType, setSelectedUpgradeType] = useState('upgrade')
  const [openCreateGroupModal, setOpenCreateGroupModal] = useState(false)

  const { data: maintenanceData } = useQueryWithLoader({
    queryKey: ['maintenance', id],
    queryFn: async () => {
      showLoader()
      const response = await baseServiceGetByColumn(
        entities.maintenances.getById(id)
      )
      return response.result
    },
    enabled: !!id,
    select: (data) => {
      return {
        ...data,
        equipment: data.equipment
          ? `${data.equipment?.number} - ${data.equipment?.name}`
          : '-',
        equipmentData: data.equipment,
        product: data.product_item?.product
          ? `${data.product_item?.code} - ${data.product_item?.product?.name}`
          : '-',
        extra_observation: data.equipment?.extra_observation ?? '-',
        product_quantity: data.product_quantity ?? '-',
        maintenance_type: data.maintenance_type?.name ?? '-',
        isDeleted: !!data.deleted_at,
      }
    },
  })

  const {
    filters: maintenanceItemsFilters,
    setFilters: applyMaintenanceItemsFilters,
    pagination: maintenanceItemsPagination,
    changePage: changeMaintenanceItemsPage,
    setPagination: setMaintenanceItemsPagination,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    'maintenance_items'
  )

  const {
    handleFilterApply: handleMaintenanceItemsFilterApply,
    handleFilterClear: handleMaintenanceItemsFilterClear,
  } = useFilterHandlers(applyMaintenanceItemsFilters)

  // Gerenciamento de filtros locais (para o FilterForm)
  const { localFilterValues: localMaintenanceItemsFilterValues } =
    useGenericFilter(
      {
        deleted_at: false,
      },
      handleMaintenanceItemsFilterApply,
      handleMaintenanceItemsFilterClear
    )

  const {
    data: maintenanceItemsData,
    isLoading: isLoadingMaintenanceItems,
    isFetching: isFetchingMaintenanceItems,
  } = useQueryWithLoader({
    queryKey: [
      'maintenance_items',
      id,
      maintenanceItemsFilters,
      maintenanceItemsPagination,
    ],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(maintenanceItemsFilters)
      const params = {
        filters: filtersTransformed,
        page: maintenanceItemsPagination.page,
        perPage: maintenanceItemsPagination.perPage,
        sort: { column: 'created_at', order: 'desc' },
      }
      const response = await baseServiceGet(
        entities.maintenances.items.get(id),
        params
      )
      return response.result
    },
    enabled: !!id,
    select: (data) => {
      return {
        maintenanceItems: data.data?.map((item) => ({
          id: item.id,
          maintenance_type: item.maintenance_type?.name ?? '',
          approval_status: item.approval_status ?? null,
          technician_status_id: item.technician_status_id,
          maintenance_status: item.maintenance_status?.name ?? '-',
          technician_status: item.technician_status?.name ?? '-',
          technician: item.technician?.name ?? '-',
          details: item.details ?? '-',
          document_id: item.documents?.[0]?.id ?? null,
          document_name: item.documents?.[0]?.original_file_name ?? null,
          date_service:
            formatDateFront({ rawDate: item.created_at, showTime: true }) ??
            '-',
          isDeleted: !!item.deleted_at,
        })),
        totalPages: data.last_page,
      }
    },
    placeholderData: (prev) => prev,
  })

  const uploadMaintenanceDocument = useMutation({
    mutationFn: async ({ maintenanceItemId, file }) => {
      const payload = new FormData()
      payload.append('file', file)
      return baseServicePost(
        entities.maintenances.itemDocumentCreate(maintenanceItemId),
        payload
      )
    },
    onSuccess: () => {
      showNotification('success', 'Documento anexado ao serviço.')
      queryClient.invalidateQueries({ queryKey: ['maintenance_items', id] })
      setSelectedServiceForDocument(null)
    },
  })

  const downloadMaintenanceDocument = useMutation({
    mutationFn: ({ maintenanceItemId, documentId }) =>
      api.get(
        entities.maintenances.itemDocumentDownload(
          maintenanceItemId,
          documentId
        ),
        { responseType: 'blob' }
      ),
    onSuccess: (response, { documentName }) => {
      downloadBlob(response, documentName || 'documento-manutencao')
    },
    onError: (error) => {
      console.error('Erro ao baixar documento da manutenção:', error)
      showNotification(
        'error',
        error.response?.data?.message ||
          'Não foi possível baixar o documento da manutenção.'
      )
    },
  })

  // Busca assíncrona de grupos: o catálogo tem milhares de registros, então
  // carregar tudo de uma vez deixava grupos antigos fora da lista. Buscamos
  // sob demanda conforme o usuário digita, mantendo o grupo já selecionado
  // no topo para não sumir após a escolha.
  const [groupSearchTerm, setGroupSearchTerm] = useState('')
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('')
  const [groupOptions, setGroupOptions] = useState([])
  const [isLoadingGroups, setIsLoadingGroups] = useState(false)
  const groupSearchSeqRef = useRef(0)
  const baseServiceGetRef = useRef(baseServiceGet)
  baseServiceGetRef.current = baseServiceGet

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedGroupSearch(groupSearchTerm), 400)
    return () => clearTimeout(timer)
  }, [groupSearchTerm])

  const fetchGroups = useCallback(async (searchTerm) => {
    const seq = ++groupSearchSeqRef.current
    setIsLoadingGroups(true)
    try {
      const params = {
        page: 1,
        perPage: 25,
        filters: {
          deleted_at: { $eq: 'null' },
          ...(searchTerm?.trim()
            ? { name: { $contains: searchTerm.trim() } }
            : {}),
        },
        sort: { column: 'name', order: 'asc' },
      }
      const response = await baseServiceGetRef.current(entities.groups.get, params)
      const result = response?.result
      const data = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : []
      // Ignora respostas de buscas antigas que chegaram fora de ordem.
      if (seq !== groupSearchSeqRef.current) return
      setGroupOptions(data)
    } catch {
      if (seq === groupSearchSeqRef.current) setGroupOptions([])
    } finally {
      if (seq === groupSearchSeqRef.current) setIsLoadingGroups(false)
    }
  }, [])

  useEffect(() => {
    if (!selectedUpgradeService) return

    const searchTerm = debouncedGroupSearch.trim()
    if (searchTerm.length < 2) {
      // Não consultar nem paginar o catálogo inteiro ao abrir o modal. Além de
      // evitar uma resposta pesada, invalida qualquer busca anterior em voo.
      groupSearchSeqRef.current += 1
      setGroupOptions([])
      setIsLoadingGroups(false)
      return
    }

    fetchGroups(searchTerm)
  }, [selectedUpgradeService, debouncedGroupSearch, fetchGroups])

  // O grupo escolhido pode sair do resultado após uma nova busca; mantê-lo nas
  // opções evita que o valor selecionado "suma" do campo.
  const equipmentGroups = useMemo(() => {
    if (
      selectedUpgradeGroup &&
      !groupOptions.some((g) => String(g.id) === String(selectedUpgradeGroup.id))
    ) {
      return [selectedUpgradeGroup, ...groupOptions]
    }
    return groupOptions
  }, [groupOptions, selectedUpgradeGroup])

  const decideUpgradeApproval = useMutation({
    mutationFn: async ({ itemId, approved, groupId, type }) =>
      baseServicePost(entities.maintenances.itemUpgradeApproval(itemId), {
        approved,
        ...(approved ? { group_id: groupId, type } : {}),
      }),
    onSuccess: (_response, variables) => {
      showNotification(
        'success',
        variables.approved ? 'Upgrade/downgrade aprovado.' : 'Upgrade/downgrade reprovado.'
      )
      queryClient.invalidateQueries({ queryKey: ['maintenance_items', id] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'aluguel', 'direcao'] })
      setSelectedUpgradeService(null)
      setSelectedUpgradeGroupId('')
      setSelectedUpgradeGroup(null)
      setSelectedUpgradeType('upgrade')
    },
    onError: () => showNotification('error', 'Não foi possível registrar a decisão.'),
  })

  const lastItem =
    maintenanceItemsData?.maintenanceItems?.[
      maintenanceItemsData?.maintenanceItems?.length - 1
    ]

  const { maintenanceItems = [], totalPages } = maintenanceItemsData || {}

  const columns = [
    { key: 'date_service', label: 'Data de Serviço', align: 'left' },
    { key: 'maintenance_status', label: 'Status da Manutenção', align: 'left' },
    { key: 'technician_status', label: 'Situação', align: 'left' },
    { key: 'technician', label: 'Técnico', align: 'left' },
    { key: 'approval_status_label', label: 'Aprovação', align: 'left' },
    { key: 'details', label: 'Detalhes', align: 'left' },
  ]

  const maintenanceItemActions = [
    {
      id: 'upgrade-approval',
      icon: faClipboardCheck,
      title: 'Analisar upgrade/downgrade',
      buttonClass: 'btn-warning',
      onClick: (item) => {
        setSelectedUpgradeService(item)
        setSelectedUpgradeGroupId('')
        setSelectedUpgradeGroup(null)
        setSelectedUpgradeType('upgrade')
        setGroupSearchTerm('')
        setDebouncedGroupSearch('')
      },
      show: (item) => canDecideUpgrade && item.approval_status === 'pending',
    },
    {
      id: 'maintenance-document',
      icon: faPaperclip,
      title: 'Anexar / visualizar documento',
      buttonClass: 'btn-info',
      onClick: (item) => setSelectedServiceForDocument(item),
      show: (item) => [6, 7].includes(Number(item.technician_status_id)),
    },
  ]

  // Remover o campo de equipamento ou produto quando necessário
  useEffect(() => {
    if (!maintenanceData) return

    const { equipment, product } = maintenanceData
    let updateFields = [...maintenanceFields.fields]

    const shouldRemoveProduct = equipment !== '-' && product === '-'
    const shouldRemoveEquipment = product !== '-' && equipment === '-'

    if (shouldRemoveProduct || shouldRemoveEquipment) {
      const fieldToRemove = shouldRemoveProduct
        ? ['maintenance.product', 'maintenance.product_quantity']
        : ['maintenance.equipment']

      updateFields = removeFields({
        arrayDependente: updateFields,
        field_id: fieldToRemove,
      })

      updateFields = updateField({
        arrayDependente: updateFields,
        field_id: shouldRemoveProduct ? 'maintenance.maintenance_type' : '',
        updates: {
          fullWidth: false,
        },
      })
    }

    setFields([
      {
        section: maintenanceFields.section,
        fields: updateFields,
      },
    ])
  }, [
    maintenanceData,
    maintenanceFields.fields,
    maintenanceFields.section,
    removeFields,
    updateField,
  ])

  useEffect(() => {
    if (maintenanceData) {
      hideLoader()
      setFormData(maintenanceData)
    }
  }, [hideLoader, maintenanceData, setFormData])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Detalhes da Manutenção"
        showBackButton={true}
        backUrl="/manutencao/"
      />

      {/* 🆕 ALERTA DE GARANTIA DO EQUIPAMENTO */}
      {(() => {
        const eq = maintenanceData?.equipmentData
        // Só mostra alerta se a manutenção for de EQUIPAMENTO (não de insumo)
        if (!eq) return null

        const hasWarranty = !!eq.warranty_date
        const inWarranty = !!eq.in_warranty

        if (!hasWarranty) {
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>EQUIPAMENTO SEM GARANTIA REGISTRADA</strong>
            </Alert>
          )
        }

        if (inWarranty) {
          return (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>EQUIPAMENTO EM GARANTIA</strong>
              {' — '}vence em{' '}
              {formatDateFront({ rawDate: eq.warranty_date, showTime: false })}
              {eq.warranty_days_left != null && (
                <> • {eq.warranty_days_left} dias restantes</>
              )}
            </Alert>
          )
        }

        return (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>GARANTIA VENCIDA</strong> em{' '}
            {formatDateFront({ rawDate: eq.warranty_date, showTime: false })}
          </Alert>
        )
      })()}

      <DetailsSectionRenderer
        sections={fields}
        formData={formData}
        readOnly={true}
      />

      {/* Verificar se a manutenção foi concluída */}
      {lastItem?.technician_status !== TechnicianStatus.RELEASED.label &&
        lastItem?.technician_status !==
          TechnicianStatus.WITHOUT_REPAIR.label && (
          <>
            <Box className="mt-4 d-flex justify-content-between align-items-center">
              <Typography className="text-primary fw-bold text-uppercase text-sm mb-1">
                Serviços
              </Typography>
              <Button
                text={'Adicionar Serviço'}
                onClick={() => setOpenAddServiceMaintenanceModal(true)}
              />
            </Box>

            <AddItemMaintenanceModal
              maintenance={maintenanceData}
              open={openAddServiceMaintenanceModal}
              onClose={() => setOpenAddServiceMaintenanceModal(false)}
            />
          </>
        )}

      <TableWithInactiveFilter
        data={maintenanceItems.map((item) => ({
          ...item,
          approval_status_label: item.approval_status
            ? ({ pending: 'PENDENTE', approved: 'APROVADO', rejected: 'REPROVADO' }[item.approval_status] ?? item.approval_status)
            : '-',
        }))}
        columns={columns}
        actions={maintenanceItemActions}
        totalPages={totalPages}
        currentPage={maintenanceItemsPagination.page - 1}
        onPageChange={changeMaintenanceItemsPage}
        rowsPerPage={maintenanceItemsPagination.perPage}
        onRowsPerPageChange={(event) => {
          const newPerPage = +event.target.value
          setMaintenanceItemsPagination({ page: 1, perPage: newPerPage })
        }}
        filterValues={localMaintenanceItemsFilterValues}
        localFilterValues={localMaintenanceItemsFilterValues}
        applyUrlFilters={applyMaintenanceItemsFilters}
        type="default"
        isLoading={isLoadingMaintenanceItems}
        isFetching={isFetchingMaintenanceItems}
      />

      <Dialog
        open={!!selectedUpgradeService}
        onClose={() => setSelectedUpgradeService(null)}
        fullWidth
        maxWidth="sm">
        <DialogTitle>Aprovação de Upgrade/Downgrade</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {selectedUpgradeService?.details}
          </Typography>
          <Typography sx={{ mb: 1, fontWeight: 500 }}>
            Tipo de alteração
          </Typography>
          <ToggleButtonGroup
            value={selectedUpgradeType}
            exclusive
            onChange={(_event, value) => value && setSelectedUpgradeType(value)}
            size="small"
            sx={{ mb: 2 }}>
            <ToggleButton value="upgrade">Upgrade</ToggleButton>
            <ToggleButton value="downgrade">Downgrade</ToggleButton>
          </ToggleButtonGroup>
          <Autocomplete
            options={equipmentGroups}
            value={selectedUpgradeGroup}
            getOptionLabel={(group) => group?.name ?? ''}
            isOptionEqualToValue={(option, value) =>
              String(option?.id) === String(value?.id)
            }
            onChange={(_event, group) => {
              setSelectedUpgradeGroup(group ?? null)
              setSelectedUpgradeGroupId(group?.id ?? '')
            }}
            onInputChange={(_event, newInputValue, reason) => {
              // Ignora o reset automático do MUI ao reabrir com valor selecionado.
              if (reason === 'reset') return
              setGroupSearchTerm(newInputValue ?? '')
            }}
            filterOptions={(options) => options}
            loading={isLoadingGroups}
            noOptionsText={
              isLoadingGroups
                ? 'Buscando grupos...'
                : groupSearchTerm.trim().length < 2
                  ? 'Digite pelo menos 2 caracteres para buscar'
                  : 'Nenhum grupo encontrado'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Novo grupo do equipamento"
                placeholder="Digite pelo menos 2 caracteres"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingGroups ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {!isLoadingGroups &&
            equipmentGroups.length === 0 &&
            !groupSearchTerm.trim() && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Nenhum grupo cadastrado. Cadastre um grupo para aprovar a solicitação.
              </Alert>
            )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <MuiButton onClick={() => setOpenCreateGroupModal(true)}>
            Cadastrar novo grupo
          </MuiButton>
          <Box>
            <MuiButton
              color="error"
              disabled={decideUpgradeApproval.isPending}
              onClick={() => decideUpgradeApproval.mutate({ itemId: selectedUpgradeService?.id, approved: false })}>
              Reprovar
            </MuiButton>
            <MuiButton
              variant="contained"
              disabled={!selectedUpgradeGroupId || decideUpgradeApproval.isPending}
              onClick={() => decideUpgradeApproval.mutate({
                itemId: selectedUpgradeService?.id,
                approved: true,
                groupId: selectedUpgradeGroupId,
                type: selectedUpgradeType,
              })}>
              Aprovar
            </MuiButton>
          </Box>
        </DialogActions>
      </Dialog>

      <CreateGroupModal
        open={openCreateGroupModal}
        onClose={() => setOpenCreateGroupModal(false)}
        onCreated={(group) => {
          if (!group?.id) return
          setSelectedUpgradeGroup(group)
          setSelectedUpgradeGroupId(group.id)
          setGroupOptions((prev) =>
            prev.some((g) => String(g.id) === String(group.id))
              ? prev
              : [group, ...prev]
          )
        }}
      />

      <Dialog
        open={!!selectedServiceForDocument}
        onClose={() => setSelectedServiceForDocument(null)}
        fullWidth
        maxWidth="sm">
        <DialogTitle>
          Documento do serviço — {selectedServiceForDocument?.technician_status}
        </DialogTitle>
        <DialogContent>
          {selectedServiceForDocument?.document_id ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Anexo atual: {selectedServiceForDocument.document_name}
              </Typography>
              <MuiButton
                variant="outlined"
                disabled={downloadMaintenanceDocument.isPending}
                onClick={() =>
                  downloadMaintenanceDocument.mutate({
                    maintenanceItemId: selectedServiceForDocument.id,
                    documentId: selectedServiceForDocument.document_id,
                    documentName: selectedServiceForDocument.document_name,
                  })
                }>
                Baixar documento
              </MuiButton>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este serviço ainda não possui documento anexado.
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            {selectedServiceForDocument?.document_id
              ? 'Selecione um arquivo para substituir o anexo atual.'
              : 'Selecione o documento do serviço.'}
          </Typography>
          <input
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            disabled={uploadMaintenanceDocument.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file && selectedServiceForDocument?.id) {
                uploadMaintenanceDocument.mutate({
                  maintenanceItemId: selectedServiceForDocument.id,
                  file,
                })
              }
              event.target.value = ''
            }}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setSelectedServiceForDocument(null)}>
            Fechar
          </MuiButton>
        </DialogActions>
      </Dialog>
    </MainLayout>
  )
}

export default DetailsMaintenancePage
